/**
 * @fileoverview CreatedInvitesTable component displays invites with responsive layouts.
 * Shows a table on desktop and card layout on mobile for better mobile usability.
 *
 * CreatedInvitesTable Component
 *
 * Displays invites created by the user. Shows as a table on desktop and card layout on mobile.
 */
import styles from './CreatedInvitesTable.module.css';

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const isExpired = (expirationDate) => {
    return new Date(expirationDate) < new Date();
};

function CreatedInvitesTable({ createdInvites, handleDeactivate, handleRemoveAccess }) {
    if (createdInvites.length === 0) return null;

    const getStatusBadge = (invite) => {
        if (invite.acceptedDate && !invite.isActive) {
            return (
                <span className={styles.statusCeased}>
                    Ceased ({formatDate(invite.acceptedDate)})
                </span>
            );
        }
        if (invite.acceptedDate && invite.userIsActive) {
            return (
                <span className={styles.statusActive}>
                    Active ({formatDate(invite.acceptedDate)})
                </span>
            );
        }
        if (invite.acceptedDate) {
            return (
                <span className={styles.statusAccepted}>
                    Accepted ({formatDate(invite.acceptedDate)})
                </span>
            );
        }
        if (!invite.isActive) {
            return <span className={styles.statusDeactivated}>Deactivated</span>;
        }
        if (isExpired(invite.expirationDate)) {
            return <span className={styles.statusExpired}>Expired</span>;
        }
        return <span className={styles.statusPending}>Pending</span>;
    };

    const getActionButton = (invite) => {
        if (invite.acceptedDate && invite.userIsActive) {
            return (
                <button
                    onClick={() => handleRemoveAccess(invite.inviteKey, invite.acceptedByName || invite.acceptedByEmail)}
                    className={styles.removeButton}
                >
                    Remove Access
                </button>
            );
        }
        if (!invite.acceptedDate && invite.isActive) {
            return (
                <button
                    onClick={() => handleDeactivate(invite.inviteKey)}
                    className={styles.deactivateButton}
                >
                    Deactivate
                </button>
            );
        }
        return null;
    };

    return (
        <div className={styles.container}>
            <h3 className={styles.heading}>Invites You Created</h3>

            {/* Desktop Table View */}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.tableHeader}>
                        <tr>
                            <th className={styles.tableHeaderCell}>Budget</th>
                            <th className={styles.tableHeaderCell}>User</th>
                            <th className={styles.tableHeaderCell}>Code</th>
                            <th className={styles.tableHeaderCell}>Access Level</th>
                            <th className={styles.tableHeaderCell}>Created</th>
                            <th className={styles.tableHeaderCell}>Expires</th>
                            <th className={styles.tableHeaderCell}>Status</th>
                            <th className={styles.tableHeaderCell}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {createdInvites.map((invite) => (
                            <tr key={invite.inviteKey} className={styles.tableRow}>
                                <td className={styles.tableCell}>{invite.budgetName}</td>
                                <td className={styles.tableCell}>
                                    {invite.acceptedByName || invite.acceptedByEmail || '-'}
                                </td>
                                <td className={styles.codeCell}>
                                    {invite.inviteCode}
                                </td>
                                <td className={styles.tableCell}>
                                    {invite.accessLevel === 'member' ? 'Member' : 'Elevated Member'}
                                </td>
                                <td className={styles.tableCell}>{formatDate(invite.createdDate)}</td>
                                <td className={styles.tableCell}>{formatDate(invite.expirationDate)}</td>
                                <td className={styles.tableCell}>
                                    {getStatusBadge(invite)}
                                </td>
                                <td className={styles.tableCell}>
                                    {getActionButton(invite)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className={styles.cardContainer}>
                {createdInvites.map((invite) => (
                    <div key={invite.inviteKey} className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h5 className={styles.cardTitle}>{invite.budgetName}</h5>
                            {getStatusBadge(invite)}
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>User:</span>
                                <span className={styles.cardValue}>
                                    {invite.acceptedByName || invite.acceptedByEmail || '-'}
                                </span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Code:</span>
                                <span className={`${styles.cardValue} ${styles.codeValue}`}>
                                    {invite.inviteCode}
                                </span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Access Level:</span>
                                <span className={styles.cardValue}>
                                    {invite.accessLevel === 'member' ? 'Member' : 'Elevated Member'}
                                </span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Created:</span>
                                <span className={styles.cardValue}>{formatDate(invite.createdDate)}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Expires:</span>
                                <span className={styles.cardValue}>{formatDate(invite.expirationDate)}</span>
                            </div>
                        </div>
                        {getActionButton(invite) && (
                            <div className={styles.cardActions}>
                                {getActionButton(invite)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default CreatedInvitesTable;
